import React, { createContext, useContext, ReactNode } from "react";
import { SectionBasedProduct } from "@brandclub/common-ui";
import { ConversationMessage, ProductPreview } from "./types/conversation";
import { useConversationState } from "./hooks/useConversationState";

interface ConciergeContextType {
  conversationId: string | null;
  setConversationId: React.Dispatch<React.SetStateAction<string | null>>;
  conversationHistory: ConversationMessage[];
  setConversationHistory: React.Dispatch<
    React.SetStateAction<ConversationMessage[]>
  >;
  isActivelyStreaming: boolean;
  setIsActivelyStreaming: React.Dispatch<React.SetStateAction<boolean>>;
  streamingAIResponse: string;
  setStreamingAIResponse: React.Dispatch<React.SetStateAction<string>>;
  currentStatus: string;
  setCurrentStatus: React.Dispatch<React.SetStateAction<string>>;
  previewSearchQueries: string[];
  setPreviewSearchQueries: React.Dispatch<React.SetStateAction<string[]>>;
  previewQueryProducts: Array<{
    query: string;
    queryIndex: number;
    products: ProductPreview[];
    totalResults: number;
  }>;
  setPreviewQueryProducts: React.Dispatch<
    React.SetStateAction<
      Array<{
        query: string;
        queryIndex: number;
        products: ProductPreview[];
        totalResults: number;
      }>
    >
  >;
  isWaitingForFollowUpQueries: boolean;
  setIsWaitingForFollowUpQueries: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  productMetadataMap: Map<string, SectionBasedProduct>;
  setProductMetadataMap: React.Dispatch<
    React.SetStateAction<Map<string, SectionBasedProduct>>
  >;
  streamingProductGroups: Map<number, string[]>;
  setStreamingProductGroups: React.Dispatch<
    React.SetStateAction<Map<number, string[]>>
  >;
  followUpQuestions: string[];
  setFollowUpQuestions: React.Dispatch<React.SetStateAction<string[]>>;
  streamingFeaturedProduct: {
    product: SectionBasedProduct;
    question: string;
  } | null;
  setStreamingFeaturedProduct: React.Dispatch<
    React.SetStateAction<{
      product: SectionBasedProduct;
      question: string;
    } | null>
  >;
}

const ConciergeContext = createContext<ConciergeContextType | undefined>(
  undefined
);

export const useConciergeContext = () => {
  const context = useContext(ConciergeContext);
  if (!context) {
    throw new Error(
      "useConciergeContext must be used within ConciergeContextProvider"
    );
  }
  return context;
};

interface ConciergeContextProviderProps {
  children: ReactNode;
}

export const ConciergeContextProvider: React.FC<
  ConciergeContextProviderProps
> = ({ children }) => {
  const conversationState = useConversationState();

  return (
    <ConciergeContext.Provider value={conversationState}>
      {children}
    </ConciergeContext.Provider>
  );
};
